/**
 * Steuerprüfung L12: Empfang von E-Rechnungen. Liest XRechnung bzw. EN 16931 als XML in beiden Syntaxen (UN/CEFACT CII und
 * OASIS UBL 2.1, Rechnung und Gutschrift) sowie ZUGFeRD/Factur-X (PDF/A-3 mit eingebettetem XML) in die bekannten Felder:
 * Nummer, Datum, Leistungszeitraum, Netto, Steuer je Satz, Brutto, Währung, Aussteller und Empfänger mit Steuernummer bzw.
 * USt-IdNr., Positionen. Ohne Fremdbibliothek: eigener kleiner XML-Leser, PDF über pdf-text.ts.
 *
 * Sicherheit (vorsichtig): Eine DTD gibt es in E-Rechnungen nicht. Jede Deklaration (<!DOCTYPE, <!ENTITY …) wird deshalb
 * abgewiesen, statt sie zu übergehen; damit gibt es weder externe Entitäten (XXE) noch Entitäten-Bomben. Erlaubt sind nur die
 * fünf vordefinierten Entitäten und Zeichenreferenzen. Grenzen für Größe (5 MB XML), Tiefe und Knotenzahl; entpackte Ströme
 * im PDF ebenfalls höchstens 5 MB. Verarbeitungsanweisungen werden übergangen, nie ausgeführt.
 *
 * Das Ergebnis ist eine Sicht für Menschen und Grundlage für Hinweise. Das Original ist immer die Datei selbst (XML bzw. das
 * im PDF eingebettete XML); was hier fehlt oder nicht lesbar ist, bleibt leer.
 */
import { extractPdfEmbeddedFiles, type Inflate } from "./pdf-text.js";

export const E_INVOICE_MAX_XML_BYTES = 5 * 1024 * 1024;
const MAX_DEPTH = 64;
const MAX_NODES = 200_000;
const MAX_ATTRS = 64;

export type EInvoiceReadErrorCode =
  | "empty" | "too_large" | "dtd" | "malformed" | "encoding" | "not_einvoice" | "no_xml_in_pdf" | "pdf_unreadable";

export class EInvoiceReadError extends Error {
  constructor(readonly code: EInvoiceReadErrorCode, detail?: string) {
    super(detail ? `${code}: ${detail}` : code);
    this.name = "EInvoiceReadError";
  }
}

/** Meldung für die Person, die hochlädt */
export function eInvoiceReadErrorText(code: EInvoiceReadErrorCode, lang: "de" | "en" = "de"): string {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  switch (code) {
    case "empty": return t("Die Datei ist leer.", "The file is empty.");
    case "too_large": return t("Die E-Rechnung ist größer als 5 MB.", "The e-invoice is larger than 5 MB.");
    case "dtd": return t("Die XML-Datei enthält eine DTD bzw. Entitäten-Deklaration. Aus Sicherheitsgründen nehmen wir solche Dateien nicht an; eine E-Rechnung braucht keine.",
      "The XML file contains a DTD or entity declaration. For security reasons such files are not accepted; an e-invoice does not need one.");
    case "malformed": return t("Die XML-Datei ist beschädigt oder unvollständig.", "The XML file is damaged or incomplete.");
    case "encoding": return t("Die Zeichenkodierung der XML-Datei ist nicht lesbar (erwartet UTF-8).", "The character encoding of the XML file cannot be read (UTF-8 expected).");
    case "not_einvoice": return t("Die Datei ist keine E-Rechnung (XRechnung bzw. EN 16931 als CII oder UBL).", "The file is not an e-invoice (XRechnung or EN 16931 as CII or UBL).");
    case "no_xml_in_pdf": return t("In diesem PDF steckt keine E-Rechnung (kein ZUGFeRD/Factur-X).", "This PDF does not contain an e-invoice (no ZUGFeRD/Factur-X).");
    case "pdf_unreadable": return t("Das PDF ließ sich nicht lesen.", "The PDF could not be read.");
  }
}

// ── XML ────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface EInvoiceXmlNode {
  /** Name wie im Dokument, mit Präfix */
  name: string;
  /** Name ohne Präfix */
  local: string;
  attrs: Record<string, string>;
  children: EInvoiceXmlNode[];
  /** eigener Text (ohne Text der Kinder), Leerraum am Rand entfernt */
  text: string;
}

const NAME_START = /[A-Za-z_\u00C0-\uFFFF]/;
const NAME_CHAR = /[A-Za-z0-9_.\-:\u00B7\u00C0-\uFFFF]/;

function decodeEntities(s: string, at: number): string {
  if (!s.includes("&")) return s;
  return s.replace(/&([^;&\s]{0,12});?/g, (m, body: string) => {
    if (!m.endsWith(";")) throw new EInvoiceReadError("malformed", `Entität ohne Semikolon bei ${at}`);
    switch (body) {
      case "lt": return "<";
      case "gt": return ">";
      case "amp": return "&";
      case "quot": return "\"";
      case "apos": return "'";
    }
    const num = /^#(\d{1,7})$/.exec(body) ?? null;
    const hex = /^#x([0-9A-Fa-f]{1,6})$/.exec(body) ?? null;
    const cp = num ? Number(num[1]) : hex ? parseInt(hex[1]!, 16) : NaN;
    // Unbekannte benannte Entitäten könnten nur aus einer DTD stammen: abweisen
    if (!Number.isFinite(cp) || cp === 0 || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) throw new EInvoiceReadError("malformed", `Entität &${body};`);
    return String.fromCodePoint(cp);
  });
}

/**
 * Wohlgeformtes XML ohne DTD in einen Baum lesen. Wirft EInvoiceReadError (dtd, malformed, too_large). Namensräume werden nicht
 * aufgelöst; die Felder werden über den Namen ohne Präfix gesucht, die Art der Rechnung über den Namensraum der Wurzel.
 */
export function parseXmlSafe(text: string, opts: { maxChars?: number } = {}): EInvoiceXmlNode {
  if (text.length > (opts.maxChars ?? E_INVOICE_MAX_XML_BYTES)) throw new EInvoiceReadError("too_large");
  const s = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  let pos = 0;
  let nodes = 0;
  const stack: EInvoiceXmlNode[] = [];
  let root: EInvoiceXmlNode | null = null;
  const textParts: string[][] = [];
  const fail = (why: string): never => { throw new EInvoiceReadError("malformed", `${why} (Stelle ${pos})`); };
  const readName = (): string => {
    const start = pos;
    if (!NAME_START.test(s[pos] ?? "")) fail("Name erwartet");
    pos++;
    while (pos < s.length && NAME_CHAR.test(s[pos]!)) pos++;
    return s.slice(start, pos);
  };
  const skipWs = () => { while (pos < s.length && /\s/.test(s[pos]!)) pos++; };
  const addText = (t: string) => {
    if (!stack.length) {
      if (t.trim()) fail("Text außerhalb des Wurzelelements");
      return;
    }
    textParts[stack.length - 1]!.push(t);
  };
  while (pos < s.length) {
    const lt = s.indexOf("<", pos);
    if (lt < 0) { addText(decodeEntities(s.slice(pos), pos)); pos = s.length; break; }
    if (lt > pos) addText(decodeEntities(s.slice(pos, lt), pos));
    pos = lt;
    if (s.startsWith("<!--", pos)) {
      const end = s.indexOf("-->", pos + 4);
      if (end < 0) fail("Kommentar ohne Ende");
      pos = end + 3;
      continue;
    }
    if (s.startsWith("<![CDATA[", pos)) {
      if (!stack.length) fail("CDATA außerhalb des Wurzelelements");
      const end = s.indexOf("]]>", pos + 9);
      if (end < 0) fail("CDATA ohne Ende");
      addText(s.slice(pos + 9, end));
      pos = end + 3;
      continue;
    }
    if (s.startsWith("<!", pos)) {
      // <!DOCTYPE, <!ENTITY, <!ELEMENT … : keine DTD, keine Entitäten (XXE, Entitäten-Bomben)
      throw new EInvoiceReadError("dtd");
    }
    if (s.startsWith("<?", pos)) {
      // Verarbeitungsanweisung (auch die XML-Deklaration): nur übergehen
      const end = s.indexOf("?>", pos + 2);
      if (end < 0) fail("Verarbeitungsanweisung ohne Ende");
      pos = end + 2;
      continue;
    }
    if (s.startsWith("</", pos)) {
      pos += 2;
      const name = readName();
      skipWs();
      if (s[pos] !== ">") fail("Endtag ohne >");
      pos++;
      const open = stack.pop();
      if (!open || open.name !== name) fail(`Endtag </${name}> passt nicht`);
      open!.text = textParts.pop()!.join("").trim();
      continue;
    }
    // Starttag
    pos++;
    const name = readName();
    const attrs: Record<string, string> = {};
    let count = 0;
    for (;;) {
      const before = pos;
      skipWs();
      if (s[pos] === ">" || s.startsWith("/>", pos)) break;
      if (pos === before) fail("Leerraum vor Attribut erwartet");
      const an = readName();
      skipWs();
      if (s[pos] !== "=") fail("= erwartet");
      pos++;
      skipWs();
      const q = s[pos];
      if (q !== "\"" && q !== "'") fail("Anführungszeichen erwartet");
      const end = s.indexOf(q!, pos + 1);
      if (end < 0) fail("Attribut ohne Ende");
      const raw = s.slice(pos + 1, end);
      if (raw.includes("<")) fail("< im Attribut");
      if (an in attrs) fail(`Attribut ${an} doppelt`);
      if (++count > MAX_ATTRS) fail("zu viele Attribute");
      attrs[an] = decodeEntities(raw, pos);
      pos = end + 1;
    }
    const selfClosing = s.startsWith("/>", pos);
    pos += selfClosing ? 2 : 1;
    if (++nodes > MAX_NODES) throw new EInvoiceReadError("too_large", "zu viele Elemente");
    const colon = name.indexOf(":");
    const node: EInvoiceXmlNode = { name, local: colon >= 0 ? name.slice(colon + 1) : name, attrs, children: [], text: "" };
    if (stack.length) stack[stack.length - 1]!.children.push(node);
    else if (root) fail("zweites Wurzelelement");
    else root = node;
    if (!selfClosing) {
      if (stack.length >= MAX_DEPTH) throw new EInvoiceReadError("too_large", "zu tief verschachtelt");
      stack.push(node);
      textParts.push([]);
    }
  }
  if (stack.length) fail("Element nicht geschlossen");
  if (!root) fail("kein Wurzelelement");
  return root!;
}

/** Bytes als Text: UTF-8 (Standard), UTF-16 mit BOM oder die in der XML-Deklaration genannte Kodierung. */
export function decodeXmlBytes(bytes: Uint8Array): string {
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return decodeWith("utf-16be", bytes.subarray(2));
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return decodeWith("utf-16le", bytes.subarray(2));
  const head = String.fromCharCode(...bytes.subarray(0, 200));
  const enc = /^\s*(?:\uFEFF|\u00EF\u00BB\u00BF)?<\?xml[^>]*encoding\s*=\s*["']([A-Za-z0-9._-]+)["']/.exec(head)?.[1]?.toLowerCase() ?? "utf-8";
  const label = ["utf-8", "utf8"].includes(enc) ? "utf-8" : ["iso-8859-1", "latin1", "iso-8859-15", "windows-1252", "cp1252", "us-ascii", "ascii"].includes(enc) ? "latin1" : null;
  if (!label) throw new EInvoiceReadError("encoding", enc);
  return decodeWith(label, bytes);
}
function decodeWith(label: string, bytes: Uint8Array): string {
  try {
    return new TextDecoder(label, { fatal: true }).decode(bytes);
  } catch {
    throw new EInvoiceReadError("encoding", label);
  }
}

// ── Zugriff auf den Baum ───────────────────────────────────────────────────────────────────────────────────────────

const kids = (n: EInvoiceXmlNode | null | undefined, local: string): EInvoiceXmlNode[] => (n ? n.children.filter((c) => c.local === local) : []);
/** erstes Element entlang eines Pfads von Namen ohne Präfix */
function at(n: EInvoiceXmlNode | null | undefined, ...path: string[]): EInvoiceXmlNode | null {
  let cur: EInvoiceXmlNode | null = n ?? null;
  for (const p of path) {
    cur = cur ? kids(cur, p)[0] ?? null : null;
    if (!cur) return null;
  }
  return cur;
}
function txt(n: EInvoiceXmlNode | null | undefined, ...path: string[]): string | null {
  const v = at(n, ...path)?.text ?? "";
  return v ? v.replace(/\s+/g, " ").trim().slice(0, 1000) : null;
}

/** Dezimalzahl aus dem XML („1234.5“, auch „-0.50“) in Cent, kaufmännisch gerundet; Unlesbares = null */
export function decimalToCents(v: string | null | undefined): number | null {
  const m = /^\s*([+-])?(\d{1,15})(?:\.(\d{1,12}))?\s*$/.exec(v ?? "");
  if (!m) return null;
  const frac = (m[3] ?? "").padEnd(3, "0");
  let cents = Number(m[2]) * 100 + Number(frac.slice(0, 2));
  if (Number(frac[2]) >= 5) cents += 1;
  return m[1] === "-" && cents !== 0 ? -cents : cents;
}
const numberOf = (v: string | null | undefined): number | null => {
  if (!v || !/^\s*[+-]?\d+(\.\d+)?\s*$/.test(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Datum: CII-Format 102 (JJJJMMTT) bzw. ISO (UBL); sonst null */
function dateOf(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(s) ?? /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
  return d.getUTCDate() === +m[3]! && d.getUTCMonth() === +m[2]! - 1 ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

// ── Ergebnis ───────────────────────────────────────────────────────────────────────────────────────────────────────

export type EInvoiceSyntax = "cii" | "ubl";

export interface EInvoiceReadParty {
  name: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  vatId: string | null;
  taxNumber: string | null;
  email: string | null;
}

export interface EInvoiceReadTax {
  category: string | null;
  ratePercent: number | null;
  basisCents: number | null;
  taxCents: number | null;
  exemptionReason: string | null;
}

export interface EInvoiceReadLine {
  id: string | null;
  name: string | null;
  quantity: number | null;
  unitCode: string | null;
  unitPriceCents: number | null;
  netCents: number | null;
  ratePercent: number | null;
}

export interface EInvoiceData {
  syntax: EInvoiceSyntax;
  /** Spezifikation (BT-24), z. B. XRechnung 3.0 oder ZUGFeRD-Profil */
  profile: string | null;
  /** Rechnungsart (BT-3): 380 Rechnung, 381 Korrektur, 384, 389 Gutschrift … */
  typeCode: string | null;
  number: string | null;
  issueDate: string | null;
  dueDate: string | null;
  currency: string | null;
  serviceFrom: string | null;
  serviceTo: string | null;
  deliveryDate: string | null;
  buyerReference: string | null;
  orderReference: string | null;
  precedingNumber: string | null;
  paymentTerms: string | null;
  iban: string | null;
  notes: string[];
  seller: EInvoiceReadParty;
  buyer: EInvoiceReadParty;
  totals: {
    lineCents: number | null;
    netCents: number | null;
    taxCents: number | null;
    grossCents: number | null;
    prepaidCents: number | null;
    payableCents: number | null;
  };
  taxes: EInvoiceReadTax[];
  lines: EInvoiceReadLine[];
}

const NS_CII = "urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100";
const NS_UBL_INVOICE = "urn:oasis:names:specification:ubl:schema:xsd:Invoice-2";
const NS_UBL_CREDIT = "urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2";

function rootNamespace(root: EInvoiceXmlNode): string | null {
  const colon = root.name.indexOf(":");
  const key = colon >= 0 ? `xmlns:${root.name.slice(0, colon)}` : "xmlns";
  return root.attrs[key] ?? null;
}

/** Syntax aus Wurzel und Namensraum; null = keine E-Rechnung */
export function eInvoiceSyntaxOf(root: EInvoiceXmlNode): EInvoiceSyntax | null {
  const ns = rootNamespace(root);
  if (root.local === "CrossIndustryInvoice" && ns === NS_CII) return "cii";
  if ((root.local === "Invoice" && ns === NS_UBL_INVOICE) || (root.local === "CreditNote" && ns === NS_UBL_CREDIT)) return "ubl";
  return null;
}

const emptyParty = (): EInvoiceReadParty => ({ name: null, street: null, postalCode: null, city: null, country: null, vatId: null, taxNumber: null, email: null });

function ciiParty(p: EInvoiceXmlNode | null): EInvoiceReadParty {
  if (!p) return emptyParty();
  const addr = at(p, "PostalTradeAddress");
  const regs = kids(p, "SpecifiedTaxRegistration").map((r) => at(r, "ID")).filter((x): x is EInvoiceXmlNode => !!x);
  const reg = (scheme: string) => regs.find((r) => (r.attrs["schemeID"] ?? "").toUpperCase() === scheme)?.text || null;
  return {
    name: txt(p, "Name"),
    street: [txt(addr, "LineOne"), txt(addr, "LineTwo")].filter(Boolean).join(", ") || null,
    postalCode: txt(addr, "PostcodeCode"), city: txt(addr, "CityName"), country: txt(addr, "CountryID")?.toUpperCase() ?? null,
    vatId: reg("VA"), taxNumber: reg("FC"),
    email: txt(p, "URIUniversalCommunication", "URIID") ?? txt(p, "DefinedTradeContact", "EmailURIUniversalCommunication", "URIID"),
  };
}

function readCii(root: EInvoiceXmlNode): EInvoiceData {
  const doc = at(root, "ExchangedDocument");
  const tx = at(root, "SupplyChainTradeTransaction");
  const agreement = at(tx, "ApplicableHeaderTradeAgreement");
  const settlement = at(tx, "ApplicableHeaderTradeSettlement");
  const sum = at(settlement, "SpecifiedTradeSettlementHeaderMonetarySummation");
  const currency = txt(settlement, "InvoiceCurrencyCode")?.toUpperCase() ?? null;
  // Gesamtsteuer in der Rechnungswährung (ein zweites TaxTotalAmount kann die Steuerwährung tragen, BT-111)
  const taxTotals = kids(sum, "TaxTotalAmount");
  const taxTotal = taxTotals.find((x) => !x.attrs["currencyID"] || x.attrs["currencyID"]?.toUpperCase() === currency) ?? taxTotals[0] ?? null;
  const period = at(settlement, "BillingSpecifiedPeriod");
  const terms = kids(settlement, "SpecifiedTradePaymentTerms");
  return {
    syntax: "cii",
    profile: txt(root, "ExchangedDocumentContext", "GuidelineSpecifiedDocumentContextParameter", "ID"),
    typeCode: txt(doc, "TypeCode"),
    number: txt(doc, "ID"),
    issueDate: dateOf(txt(doc, "IssueDateTime", "DateTimeString")),
    dueDate: dateOf(terms.map((t) => txt(t, "DueDateDateTime", "DateTimeString")).find(Boolean)),
    currency,
    serviceFrom: dateOf(txt(period, "StartDateTime", "DateTimeString")),
    serviceTo: dateOf(txt(period, "EndDateTime", "DateTimeString")),
    deliveryDate: dateOf(txt(tx, "ApplicableHeaderTradeDelivery", "ActualDeliverySupplyChainEvent", "OccurrenceDateTime", "DateTimeString")),
    buyerReference: txt(agreement, "BuyerReference"),
    orderReference: txt(agreement, "BuyerOrderReferencedDocument", "IssuerAssignedID"),
    precedingNumber: txt(settlement, "InvoiceReferencedDocument", "IssuerAssignedID"),
    paymentTerms: terms.map((t) => txt(t, "Description")).find(Boolean) ?? null,
    iban: kids(settlement, "SpecifiedTradeSettlementPaymentMeans").map((m) => txt(m, "PayeePartyCreditorFinancialAccount", "IBANID")).find(Boolean) ?? null,
    notes: kids(doc, "IncludedNote").map((n) => txt(n, "Content")).filter((x): x is string => !!x).slice(0, 20),
    seller: ciiParty(at(agreement, "SellerTradeParty")),
    buyer: ciiParty(at(agreement, "BuyerTradeParty")),
    totals: {
      lineCents: decimalToCents(txt(sum, "LineTotalAmount")),
      netCents: decimalToCents(txt(sum, "TaxBasisTotalAmount")),
      taxCents: decimalToCents(taxTotal?.text),
      grossCents: decimalToCents(txt(sum, "GrandTotalAmount")),
      prepaidCents: decimalToCents(txt(sum, "TotalPrepaidAmount")),
      payableCents: decimalToCents(txt(sum, "DuePayableAmount")),
    },
    taxes: kids(settlement, "ApplicableTradeTax").map((t) => ({
      category: txt(t, "CategoryCode"), ratePercent: numberOf(txt(t, "RateApplicablePercent")), basisCents: decimalToCents(txt(t, "BasisAmount")),
      taxCents: decimalToCents(txt(t, "CalculatedAmount")), exemptionReason: txt(t, "ExemptionReason"),
    })),
    lines: kids(tx, "IncludedSupplyChainTradeLineItem").map((l) => {
      const price = at(l, "SpecifiedLineTradeAgreement", "NetPriceProductTradePrice");
      const qty = at(l, "SpecifiedLineTradeDelivery", "BilledQuantity");
      const ls = at(l, "SpecifiedLineTradeSettlement");
      return {
        id: txt(l, "AssociatedDocumentLineDocument", "LineID"), name: txt(l, "SpecifiedTradeProduct", "Name"),
        quantity: numberOf(qty?.text), unitCode: qty?.attrs["unitCode"] ?? null, unitPriceCents: decimalToCents(txt(price, "ChargeAmount")),
        netCents: decimalToCents(txt(ls, "SpecifiedTradeSettlementLineMonetarySummation", "LineTotalAmount")),
        ratePercent: numberOf(txt(ls, "ApplicableTradeTax", "RateApplicablePercent")),
      };
    }),
  };
}

function ublParty(p: EInvoiceXmlNode | null): EInvoiceReadParty {
  if (!p) return emptyParty();
  const addr = at(p, "PostalAddress");
  const schemes = kids(p, "PartyTaxScheme");
  const vat = schemes.find((s) => (txt(s, "TaxScheme", "ID") ?? "").toUpperCase() === "VAT");
  const other = schemes.find((s) => s !== vat);
  return {
    name: txt(p, "PartyName", "Name") ?? txt(p, "PartyLegalEntity", "RegistrationName"),
    street: [txt(addr, "StreetName"), txt(addr, "AdditionalStreetName")].filter(Boolean).join(", ") || null,
    postalCode: txt(addr, "PostalZone"), city: txt(addr, "CityName"), country: txt(addr, "Country", "IdentificationCode")?.toUpperCase() ?? null,
    vatId: vat ? txt(vat, "CompanyID") : null, taxNumber: other ? txt(other, "CompanyID") : null,
    email: txt(p, "Contact", "ElectronicMail") ?? (at(p, "EndpointID")?.attrs["schemeID"] === "EM" ? txt(p, "EndpointID") : null),
  };
}

function readUbl(root: EInvoiceXmlNode): EInvoiceData {
  const credit = root.local === "CreditNote";
  const currency = txt(root, "DocumentCurrencyCode")?.toUpperCase() ?? null;
  const sum = at(root, "LegalMonetaryTotal");
  const taxTotals = kids(root, "TaxTotal");
  // Steuer in der Rechnungswährung; die Aufschlüsselung je Satz steht nur dort (BG-23)
  const main = taxTotals.find((t) => (at(t, "TaxAmount")?.attrs["currencyID"] ?? currency)?.toUpperCase() === currency) ?? taxTotals[0] ?? null;
  const period = at(root, "InvoicePeriod");
  return {
    syntax: "ubl",
    profile: txt(root, "CustomizationID"),
    typeCode: txt(root, credit ? "CreditNoteTypeCode" : "InvoiceTypeCode"),
    number: txt(root, "ID"),
    issueDate: dateOf(txt(root, "IssueDate")),
    dueDate: dateOf(txt(root, "DueDate") ?? txt(root, "PaymentMeans", "PaymentDueDate")),
    currency,
    serviceFrom: dateOf(txt(period, "StartDate")),
    serviceTo: dateOf(txt(period, "EndDate")),
    deliveryDate: dateOf(txt(root, "Delivery", "ActualDeliveryDate")),
    buyerReference: txt(root, "BuyerReference"),
    orderReference: txt(root, "OrderReference", "ID"),
    precedingNumber: txt(root, "BillingReference", "InvoiceDocumentReference", "ID"),
    paymentTerms: txt(root, "PaymentTerms", "Note"),
    iban: kids(root, "PaymentMeans").map((m) => txt(m, "PayeeFinancialAccount", "ID")).find(Boolean) ?? null,
    notes: kids(root, "Note").map((n) => n.text.replace(/\s+/g, " ").trim()).filter(Boolean).slice(0, 20),
    seller: ublParty(at(root, "AccountingSupplierParty", "Party")),
    buyer: ublParty(at(root, "AccountingCustomerParty", "Party")),
    totals: {
      lineCents: decimalToCents(txt(sum, "LineExtensionAmount")),
      netCents: decimalToCents(txt(sum, "TaxExclusiveAmount")),
      taxCents: decimalToCents(txt(main, "TaxAmount")),
      grossCents: decimalToCents(txt(sum, "TaxInclusiveAmount")),
      prepaidCents: decimalToCents(txt(sum, "PrepaidAmount")),
      payableCents: decimalToCents(txt(sum, "PayableAmount")),
    },
    taxes: kids(main, "TaxSubtotal").map((t) => ({
      category: txt(t, "TaxCategory", "ID"), ratePercent: numberOf(txt(t, "TaxCategory", "Percent")), basisCents: decimalToCents(txt(t, "TaxableAmount")),
      taxCents: decimalToCents(txt(t, "TaxAmount")), exemptionReason: txt(t, "TaxCategory", "TaxExemptionReason"),
    })),
    lines: kids(root, credit ? "CreditNoteLine" : "InvoiceLine").map((l) => {
      const qty = at(l, credit ? "CreditedQuantity" : "InvoicedQuantity");
      return {
        id: txt(l, "ID"), name: txt(l, "Item", "Name"), quantity: numberOf(qty?.text), unitCode: qty?.attrs["unitCode"] ?? null,
        unitPriceCents: decimalToCents(txt(l, "Price", "PriceAmount")), netCents: decimalToCents(txt(l, "LineExtensionAmount")),
        ratePercent: numberOf(txt(l, "Item", "ClassifiedTaxCategory", "Percent")),
      };
    }),
  };
}

/** XML-Text einer E-Rechnung lesen (CII oder UBL). Wirft EInvoiceReadError. */
export function readEInvoiceXml(text: string): EInvoiceData {
  if (!text.trim()) throw new EInvoiceReadError("empty");
  const root = parseXmlSafe(text);
  const syntax = eInvoiceSyntaxOf(root);
  if (!syntax) throw new EInvoiceReadError("not_einvoice", root.name);
  return syntax === "cii" ? readCii(root) : readUbl(root);
}

// ── Datei (XML oder PDF) ───────────────────────────────────────────────────────────────────────────────────────────

/** Namen des eingebetteten XML in ZUGFeRD 1/2, Factur-X und XRechnung im PDF, in dieser Reihenfolge */
export const E_INVOICE_PDF_NAMES = ["factur-x.xml", "zugferd-invoice.xml", "xrechnung.xml"] as const;

export interface EInvoiceFile {
  container: "xml" | "pdf";
  /** das XML unverändert (bei XML die Datei selbst, beim PDF der eingebettete Anhang) */
  xml: Uint8Array;
  /** Dateiname des XML (beim PDF der Name des Anhangs) */
  xmlName: string | null;
  data: EInvoiceData;
}

export type EInvoiceKind = "xml" | "pdf" | "other";

/** Art einer Datei nach ihrem Inhalt (nicht nach Name oder Inhaltstyp) */
export function sniffEInvoiceKind(bytes: Uint8Array): EInvoiceKind {
  let i = 0;
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) i = 3;
  else if ((bytes[0] === 0xfe && bytes[1] === 0xff) || (bytes[0] === 0xff && bytes[1] === 0xfe)) return "xml";
  const head = String.fromCharCode(...bytes.subarray(i, i + 1024));
  if (head.trimStart().startsWith("<")) return "xml";
  return head.includes("%PDF-") ? "pdf" : "other";
}

/**
 * Entpacken mit Obergrenze (Schutz vor gepackten Bomben im PDF): bricht ab, sobald mehr als `limit` Bytes entstehen.
 * Wie defaultInflate (pdf-text.ts) zlib, sonst roh.
 */
export function boundedInflate(limit: number = E_INVOICE_MAX_XML_BYTES): Inflate {
  const run = async (fmt: string, data: Uint8Array): Promise<Uint8Array> => {
    const DS = (globalThis as { DecompressionStream?: new (f: string) => TransformStream<Uint8Array, Uint8Array> }).DecompressionStream;
    if (!DS) throw new Error("Entpacken wird hier nicht unterstützt.");
    const reader = new Blob([data as BlobPart]).stream().pipeThrough(new DS(fmt)).getReader();
    const parts: Uint8Array[] = [];
    let n = 0;
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        n += value.length;
        if (n > limit) {
          await reader.cancel().catch(() => undefined);
          throw new EInvoiceReadError("too_large", "entpackter Strom");
        }
        parts.push(value);
      }
    } catch (e) {
      if (e instanceof EInvoiceReadError || !parts.length) throw e;
    }
    const out = new Uint8Array(n);
    let o = 0;
    for (const p of parts) { out.set(p, o); o += p.length; }
    return out;
  };
  return async (data) => {
    try {
      return await run("deflate", data);
    } catch (e) {
      if (e instanceof EInvoiceReadError) throw e;
      return run("deflate-raw", data.subarray(2));
    }
  };
}

/**
 * E-Rechnung aus einer Datei lesen: XML direkt, PDF über den eingebetteten Anhang (factur-x.xml, zugferd-invoice.xml,
 * xrechnung.xml, sonst der erste XML-Anhang, der eine E-Rechnung ist). Wirft EInvoiceReadError.
 */
export async function readEInvoiceFile(bytes: Uint8Array, opts: { fileName?: string | null; inflate?: Inflate; maxXmlBytes?: number } = {}): Promise<EInvoiceFile> {
  const max = opts.maxXmlBytes ?? E_INVOICE_MAX_XML_BYTES;
  if (!bytes.length) throw new EInvoiceReadError("empty");
  const kind = sniffEInvoiceKind(bytes);
  if (kind === "xml") {
    if (bytes.length > max) throw new EInvoiceReadError("too_large");
    return { container: "xml", xml: bytes, xmlName: opts.fileName ?? null, data: readEInvoiceXml(decodeXmlBytes(bytes)) };
  }
  if (kind !== "pdf") throw new EInvoiceReadError("not_einvoice");
  let files: { name: string; bytes: Uint8Array }[];
  try {
    files = await extractPdfEmbeddedFiles(bytes, { inflate: opts.inflate ?? boundedInflate(max) });
  } catch (e) {
    if (e instanceof EInvoiceReadError) throw e;
    throw new EInvoiceReadError("pdf_unreadable");
  }
  const xmls = files.filter((f) => /\.xml$/i.test(f.name) && f.bytes.length > 0);
  const rank = (n: string) => {
    const i = E_INVOICE_PDF_NAMES.findIndex((x) => x.toLowerCase() === n.toLowerCase());
    return i < 0 ? 99 : i;
  };
  xmls.sort((a, b) => rank(a.name) - rank(b.name));
  let firstError: EInvoiceReadError | null = null;
  for (const f of xmls) {
    if (f.bytes.length > max) { firstError ??= new EInvoiceReadError("too_large"); continue; }
    try {
      return { container: "pdf", xml: f.bytes, xmlName: f.name, data: readEInvoiceXml(decodeXmlBytes(f.bytes)) };
    } catch (e) {
      if (!(e instanceof EInvoiceReadError)) throw e;
      // ein bekannter ZUGFeRD-Name mit kaputtem Inhalt zählt als Fehler; andere XML-Anhänge (etwa Metadaten) nicht
      if (rank(f.name) < 99 || e.code === "dtd") firstError ??= e;
    }
  }
  throw firstError ?? new EInvoiceReadError("no_xml_in_pdf");
}

// ── Prüfungen und Abweichungen ─────────────────────────────────────────────────────────────────────────────────────

export interface EInvoiceFinding { key: string; text: string }

const euro = (c: number) => (c / 100).toFixed(2);

/** Innere Stimmigkeit der E-Rechnung (Pflichtfelder und Summen nach EN 16931, BR-CO-10/13/15), als Hinweise */
export function eInvoiceSelfCheck(d: EInvoiceData, lang: "de" | "en" = "de"): EInvoiceFinding[] {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const out: EInvoiceFinding[] = [];
  if (!d.number) out.push({ key: "number", text: t("Rechnungsnummer fehlt (BT-1).", "Invoice number missing (BT-1).") });
  if (!d.issueDate) out.push({ key: "issue_date", text: t("Rechnungsdatum fehlt oder ist unlesbar (BT-2).", "Invoice date missing or unreadable (BT-2).") });
  if (!d.currency) out.push({ key: "currency", text: t("Währung fehlt (BT-5).", "Currency missing (BT-5).") });
  if (!d.seller.name) out.push({ key: "seller", text: t("Name des Ausstellers fehlt (BT-27).", "Seller name missing (BT-27).") });
  if (!d.seller.vatId && !d.seller.taxNumber) out.push({ key: "seller_tax_id", text: t("Weder USt-IdNr. noch Steuernummer des Ausstellers (BT-31/BT-32).", "Neither VAT ID nor tax number of the seller (BT-31/BT-32).") });
  if (!d.buyer.name) out.push({ key: "buyer", text: t("Name des Empfängers fehlt (BT-44).", "Buyer name missing (BT-44).") });
  const { netCents: net, taxCents: tax, grossCents: gross, lineCents } = d.totals;
  if (net === null || gross === null) out.push({ key: "totals", text: t("Netto- oder Gesamtbetrag fehlt.", "Net or total amount missing.") });
  if (net !== null && gross !== null && net + (tax ?? 0) !== gross) {
    out.push({ key: "sum", text: t(`Netto ${euro(net)} plus Steuer ${euro(tax ?? 0)} ergibt nicht den Gesamtbetrag ${euro(gross)}.`, `Net ${euro(net)} plus tax ${euro(tax ?? 0)} does not equal the total ${euro(gross)}.`) });
  }
  const lineSum = d.lines.reduce((a, l) => a + (l.netCents ?? 0), 0);
  if (d.lines.length && lineCents !== null && lineSum !== lineCents) {
    out.push({ key: "lines", text: t(`Summe der Positionen ${euro(lineSum)} weicht von der Summe ${euro(lineCents)} ab.`, `Sum of line items ${euro(lineSum)} differs from the total ${euro(lineCents)}.`) });
  }
  const taxSum = d.taxes.reduce((a, x) => a + (x.taxCents ?? 0), 0);
  if (d.taxes.length && tax !== null && taxSum !== tax) {
    out.push({ key: "tax_breakdown", text: t(`Steuer je Satz (${euro(taxSum)}) weicht von der Gesamtsteuer ${euro(tax)} ab.`, `Tax per rate (${euro(taxSum)}) differs from the total tax ${euro(tax)}.`) });
  }
  return out;
}

/** Erwartete Werte aus dem Portal: Angaben der Rechnung, Profil, Empfänger, angeforderter Monat */
export interface EInvoiceExpected {
  number?: string | null;
  issueDate?: string | null;
  serviceFrom?: string | null;
  serviceTo?: string | null;
  /** angeforderter Leistungsmonat (Rechnungsanforderung der Agentur), 'JJJJ-MM' oder 'JJJJ-MM-TT' */
  month?: string | null;
  currency?: string | null;
  netCents?: number | null;
  taxCents?: number | null;
  grossCents?: number | null;
  sellerVatId?: string | null;
  sellerTaxNumber?: string | null;
  buyerName?: string | null;
}

const norm = (s: string | null | undefined) => (s ?? "").normalize("NFKC").replace(/[\s.\-/]/g, "").toUpperCase();
const nameNorm = (s: string | null | undefined) => (s ?? "").normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

/**
 * Abweichungen zwischen E-Rechnung und den Angaben im Portal, als Hinweise (nie Sperre). Verglichen wird nur, was auf beiden
 * Seiten vorhanden ist. Beträge sind bei Gutschriften (381) und Korrekturen positiv zu lesen; verglichen wird der Betrag.
 */
export function eInvoiceDeviations(d: EInvoiceData, e: EInvoiceExpected, lang: "de" | "en" = "de"): EInvoiceFinding[] {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const out: EInvoiceFinding[] = [];
  const amount = (key: string, label: [string, string], xml: number | null, exp: number | null | undefined) => {
    if (xml === null || exp === null || exp === undefined) return;
    if (Math.abs(xml) !== Math.abs(exp)) out.push({ key, text: t(`${label[0]}: in der E-Rechnung ${euro(xml)}, im Portal ${euro(exp)}.`, `${label[1]}: ${euro(xml)} in the e-invoice, ${euro(exp)} in the portal.`) });
  };
  if (d.number && e.number && norm(d.number) !== norm(e.number)) {
    out.push({ key: "number", text: t(`Rechnungsnummer: in der E-Rechnung „${d.number}“, im Portal „${e.number}“.`, `Invoice number: “${d.number}” in the e-invoice, “${e.number}” in the portal.`) });
  }
  if (d.currency && e.currency && d.currency !== e.currency.toUpperCase()) {
    out.push({ key: "currency", text: t(`Währung: in der E-Rechnung ${d.currency}, im Portal ${e.currency}.`, `Currency: ${d.currency} in the e-invoice, ${e.currency} in the portal.`) });
  }
  amount("gross", ["Bruttobetrag", "Gross amount"], d.totals.grossCents, e.grossCents);
  amount("net", ["Nettobetrag", "Net amount"], d.totals.netCents, e.netCents);
  amount("tax", ["Umsatzsteuer", "VAT"], d.totals.taxCents, e.taxCents);
  if (d.issueDate && e.issueDate && d.issueDate !== e.issueDate.slice(0, 10)) {
    out.push({ key: "issue_date", text: t(`Rechnungsdatum: in der E-Rechnung ${d.issueDate}, im Portal ${e.issueDate.slice(0, 10)}.`, `Invoice date: ${d.issueDate} in the e-invoice, ${e.issueDate.slice(0, 10)} in the portal.`) });
  }
  const from = d.serviceFrom ?? d.deliveryDate;
  const to = d.serviceTo ?? d.deliveryDate;
  if (from && to && e.serviceFrom && e.serviceTo && (from !== e.serviceFrom.slice(0, 10) || to !== e.serviceTo.slice(0, 10))) {
    out.push({ key: "service", text: t(`Leistungszeitraum: in der E-Rechnung ${from} bis ${to}, im Portal ${e.serviceFrom.slice(0, 10)} bis ${e.serviceTo.slice(0, 10)}.`,
      `Service period: ${from} to ${to} in the e-invoice, ${e.serviceFrom.slice(0, 10)} to ${e.serviceTo.slice(0, 10)} in the portal.`) });
  }
  const month = e.month?.slice(0, 7);
  if (month && from && to && (to.slice(0, 7) < month || from.slice(0, 7) > month)) {
    out.push({ key: "month", text: t(`Der Leistungszeitraum der E-Rechnung (${from} bis ${to}) liegt nicht im angeforderten Monat ${month}.`,
      `The service period of the e-invoice (${from} to ${to}) is not in the requested month ${month}.`) });
  }
  const ids = [d.seller.vatId, d.seller.taxNumber].filter(Boolean).map(norm);
  const known = [e.sellerVatId, e.sellerTaxNumber].filter((x): x is string => !!x && !!x.trim()).map(norm);
  if (ids.length && known.length && !ids.some((x) => known.includes(x))) {
    out.push({ key: "seller_tax_id", text: t(`Steuernummer bzw. USt-IdNr. des Ausstellers (${[d.seller.vatId, d.seller.taxNumber].filter(Boolean).join(", ")}) passt nicht zum Profil.`,
      `Seller tax number or VAT ID (${[d.seller.vatId, d.seller.taxNumber].filter(Boolean).join(", ")}) does not match the profile.`) });
  }
  if (d.buyer.name && e.buyerName) {
    const a = nameNorm(d.buyer.name);
    const b = nameNorm(e.buyerName);
    if (a && b && !a.includes(b) && !b.includes(a)) {
      out.push({ key: "buyer", text: t(`Empfänger: in der E-Rechnung „${d.buyer.name}“, erwartet „${e.buyerName}“.`, `Recipient: “${d.buyer.name}” in the e-invoice, “${e.buyerName}” expected.`) });
    }
  }
  return out;
}

// ── Kurzfassung für die Datenbank (attachments.e_invoice) ──────────────────────────────────────────────────────────

export interface EInvoiceSummary {
  v: 1;
  syntax: EInvoiceSyntax;
  container: "xml" | "pdf";
  origin: "uploaded" | "generated";
  profile: string | null;
  type_code: string | null;
  xml_name: string | null;
  /** Prüfsumme des XML selbst (beim PDF des eingebetteten Anhangs) */
  xml_sha256: string;
  number: string | null;
  issue_date: string | null;
  service_from: string | null;
  service_to: string | null;
  currency: string | null;
  net_cents: number | null;
  tax_cents: number | null;
  gross_cents: number | null;
  seller_name: string | null;
  seller_vat_id: string | null;
  seller_tax_number: string | null;
  buyer_name: string | null;
}

const cut = (s: string | null, n = 200) => (s ? s.slice(0, n) : null);

/** Kurzfassung für die Anlage: genug für Liste und Hinweise, die Sichtansicht liest immer das Original */
export function eInvoiceSummary(f: Pick<EInvoiceFile, "container" | "xmlName" | "data">, xmlSha256: string, origin: "uploaded" | "generated" = "uploaded"): EInvoiceSummary {
  const d = f.data;
  return {
    v: 1, syntax: d.syntax, container: f.container, origin, profile: cut(d.profile), type_code: cut(d.typeCode, 10), xml_name: cut(f.xmlName, 120), xml_sha256: xmlSha256,
    number: cut(d.number, 100), issue_date: d.issueDate, service_from: d.serviceFrom ?? d.deliveryDate, service_to: d.serviceTo ?? d.deliveryDate,
    currency: cut(d.currency, 3), net_cents: d.totals.netCents, tax_cents: d.totals.taxCents, gross_cents: d.totals.grossCents,
    seller_name: cut(d.seller.name), seller_vat_id: cut(d.seller.vatId, 40), seller_tax_number: cut(d.seller.taxNumber, 40), buyer_name: cut(d.buyer.name),
  };
}

/** Abweichungen aus der gespeicherten Kurzfassung (Liste, Rechnungsansicht), ohne das Original zu laden */
export function eInvoiceSummaryDeviations(s: EInvoiceSummary, e: EInvoiceExpected, lang: "de" | "en" = "de"): EInvoiceFinding[] {
  const d: EInvoiceData = {
    syntax: s.syntax, profile: s.profile, typeCode: s.type_code, number: s.number, issueDate: s.issue_date, dueDate: null, currency: s.currency,
    serviceFrom: s.service_from, serviceTo: s.service_to, deliveryDate: null, buyerReference: null, orderReference: null, precedingNumber: null, paymentTerms: null,
    iban: null, notes: [],
    seller: { ...emptyParty(), name: s.seller_name, vatId: s.seller_vat_id, taxNumber: s.seller_tax_number },
    buyer: { ...emptyParty(), name: s.buyer_name },
    totals: { lineCents: null, netCents: s.net_cents, taxCents: s.tax_cents, grossCents: s.gross_cents, prepaidCents: null, payableCents: null },
    taxes: [], lines: [],
  };
  return eInvoiceDeviations(d, e, lang);
}

/** Bezeichnung der Art für Menschen */
export function eInvoiceFormatLabel(s: Pick<EInvoiceSummary, "syntax" | "container" | "profile">, lang: "de" | "en" = "de"): string {
  const p = (s.profile ?? "").toLowerCase();
  const xr = p.includes("xrechnung");
  if (s.container === "pdf") return p.includes("factur-x") || p.includes("zugferd") || !xr ? "ZUGFeRD/Factur-X" : lang === "en" ? "XRechnung in PDF" : "XRechnung im PDF";
  return `${xr ? "XRechnung" : "EN 16931"} (${s.syntax.toUpperCase()})`;
}
